<template>
  <div>
    <div v-if="user">
      <v-navigation-drawer app right floating clipped>
        <div class="pa-8 py-6">
          <div class="d-flex flex-column align-center mb-4">
            <a id="profile-picture-trigger">
              <v-avatar color="grey" size="100" class="logo">
                <img
                  v-if="user.profilePicture"
                  :src="user.profilePicture"
                  :alt="user.username"
                />
                <v-icon v-else dark x-large>mdi-account</v-icon>
              </v-avatar>
            </a>
          </div>

          <p class="title-1 font-weight-bold">{{ getName() }}</p>
          <p class="caption">{{ user.email1 }}</p>
          <p class="caption">{{ user.contactNumber1 }}</p>
          <div v-if="showAmountDue">
            <p class="caption mt-2">Amount Due:</p>
            <p
              v-if="!amountDue || amountDue < 0"
              class="body-2 font-weight-bold"
            >
              {{ formatCurrency(amountDue) }}
            </p>
            <p v-else class="body-2 font-weight-bold red--text">
              {{ formatCurrency(amountDue) }}
            </p>
          </div>
          <div v-else>
            <p class="caption mt-2">Credits:</p>
            <p v-if="!balance || balance >= 0" class="body-2 font-weight-bold">
              {{ formatCurrency(balance) }}
            </p>
            <p v-else class="body-2 font-weight-bold red--text">
              {{ formatCurrency(balance) }}
            </p>
            <p class="caption mt-2">Pending Balance:</p>
            <p
              v-if="!pendingBalance || pendingBalance < 0"
              class="body-2 font-weight-bold"
            >
              {{ formatCurrency(pendingBalance) }}
            </p>
            <p v-else class="body-2 font-weight-bold red--text">
              {{ formatCurrency(pendingBalance) }}
            </p>
          </div>

          <v-btn
            v-if="showAmountDue"
            depressed
            x-small
            class="mt-3"
            @click="showBalance(false)"
            >Show breakdown</v-btn
          >
          <v-btn
            v-else
            depressed
            x-small
            class="mt-3"
            @click="showBalance(true)"
            >Show total</v-btn
          >

          <v-divider class="my-8"></v-divider>

          <div class="d-flex flex-row">
            <v-icon small>mdi-cake</v-icon>
            <p class="mb-0 ml-2 caption">Birthday</p>
          </div>
          <p class="subtitle-1 font-weight-bold">
            {{ getParsedDate(user.birthday) }}
          </p>

          <div class="d-flex flex-row mt-4">
            <v-icon small>mdi-account-question</v-icon>
            <p class="mb-0 ml-2 caption">User Type</p>
          </div>
          <p class="subtitle-1 font-weight-bold">{{ user.userType }}</p>

          <div class="d-flex flex-row mt-4">
            <v-icon small>mdi-calendar</v-icon>
            <p class="mb-0 ml-2 caption">Date Created</p>
          </div>
          <p class="subtitle-1 font-weight-bold">
            {{ getParsedDate(user.dateCreated) }}
          </p>

          <div class="d-flex flex-row mt-4">
            <v-icon small>mdi-calendar</v-icon>
            <p class="mb-0 ml-2 caption">Status</p>
          </div>
          <div v-if="user.activationCode">
            <p class="body-2 red--text">{{ 'Pending Activation' }}</p>
            <a
              class="caption"
              style="text-decoration: underline"
              @click="resendActivation"
              >{{ 'Resend Activation Email' }}</a
            >
          </div>
          <div v-else>
            <p class="body-2">{{ 'Activated' }}</p>
          </div>
        </div>
      </v-navigation-drawer>

      <v-container class="mx-12 my-6">
        <div class="d-flex flex-row mb-4">
          <div class="card-label d-flex flex-row mb-4">
            <v-icon>mdi-cash-multiple</v-icon>
            <p class="mb-0 ml-4 subtitle-1 font-weight-bold">
              User Transactions
            </p>
          </div>
          <div class="flex-grow-1"></div>
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-bind="attrs" icon v-on="on">
                <v-icon>mdi-dots-horizontal</v-icon>
              </v-btn>
            </template>
            <v-list dense class="pa-0">
              <v-list-item
                v-for="(item, index) in transactionItems"
                :key="index"
                @click="item.action"
              >
                <v-list-item-icon class="mr-2">
                  <v-icon small>{{ item.icon }}</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ item.title }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
        <payment-request-table
          :tenant-id="user.userId"
          :reload-trigger="paymentsSearchTrigger"
          type="user"
          @reload="reload"
        ></payment-request-table>
        <v-divider class="my-8"></v-divider>
        <paid-payment-table
          :tenant-id="user.userId"
          :reload-trigger="paymentsSearchTrigger"
          type="user"
          @reload="reload"
        ></paid-payment-table>
        <v-divider class="my-8"></v-divider>
        <div class="d-flex flex-row mb-4">
          <v-icon>mdi-cash-lock</v-icon>
          <p class="mb-0 ml-4 subtitle-2 font-weight-bold">Security Deposits</p>
        </div>
        <div v-if="user.userId" class="mb-8">
          <security-deposit-table
            :user-id="user.userId"
            :search-trigger="securityDepositSearchTrigger"
            :tenant-id="user.userId"
            type="user"
            @securityDepositReload="reload"
          ></security-deposit-table>
        </div>

        <div class="card-label d-flex flex-row mb-4">
          <v-icon>mdi-alert-outline</v-icon>
          <p class="mb-0 ml-4 subtitle-2 font-weight-bold">User Violations</p>
        </div>
        <v-card class="card-content mb-8 pa-6" elevation="0">
          <v-data-table
            v-if="violationsAccess.read"
            :headers="violationHeaders"
            :items="violations"
            :options.sync="violationOptions"
            :server-items-length="violationsTotal"
            item-key="violationId"
            :loading="loading"
            loading-text="Loading... Please wait"
            no-data-text="All Violations will appear here."
            multi-sort
            dense
            :footer-props="{ 'items-per-page-options': [5, 10, 20, 50, 100] }"
          >
            <template v-slot:item="{ expand, item, isExpanded }">
              <tr class="tr-head" @click="showViolation(item)">
                <td>
                  <p class="caption">{{ item.violationId }}</p>
                </td>
                <td>
                  <p class="caption">{{ item.date }}</p>
                </td>
                <!-- <td><p class="caption">{{item.tenantName}}</p></td> -->
                <td>
                  <p class="caption">{{ item.description }}</p>
                </td>
                <td>
                  <p class="caption">{{ item.paymentId }}</p>
                </td>
                <td>
                  <div v-if="item.attachments && item.attachments.length > 0">
                    <v-btn icon @click="downloadAttachments(item.attachments)">
                      <v-icon>mdi-attachment</v-icon>
                    </v-btn>
                  </div>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>

        <div class="card-label d-flex flex-row mb-4">
          <v-icon>mdi-account-multiple</v-icon>
          <p class="mb-0 ml-4 subtitle-2 font-weight-bold">
            Emergency Contacts
          </p>
        </div>
        <v-card class="card-content mb-8 pa-6" elevation="0">
          <div
            v-for="(contact, index) in user.emergencyContacts"
            :key="index + '-item'"
          >
            <v-list-item dense three-line>
              <v-list-item-avatar color="grey">
                <v-icon dark>mdi-account</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ contact.name }}</v-list-item-title>
                <v-list-item-subtitle>{{ contact.email }}</v-list-item-subtitle>
                <v-list-item-action-text>{{
                  contact.contactNumber
                }}</v-list-item-action-text>
              </v-list-item-content>
            </v-list-item>
          </div>
        </v-card>
      </v-container>
    </div>
    <div v-else style="width: 100%">
      <p class="title text-center">You have no access to this user's data</p>
    </div>
    <Invoice ref="invoice"></Invoice>
    <ConfirmPayment ref="confirmPayment"></ConfirmPayment>
    <VoidPayment ref="voidPayment"></VoidPayment>
    <VoidPaidPayment ref="voidPaidPayment"></VoidPaidPayment>
    <CancelPayment ref="cancelPayment"></CancelPayment>
    <CancelPaidPayment ref="cancelPaidPayment"></CancelPaidPayment>
    <UpdatePaymentRequest ref="updatePaymentRequest"></UpdatePaymentRequest>
    <CollectUtilities ref="collectUtilities"></CollectUtilities>
    <CombinedPayments ref="combinedPayments"></CombinedPayments>
    <v-snackbar v-model="snackbar" :timeout="timeout">
      {{ snackMessage }}
      <v-btn color="blue" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { dayjs } from '../util/date'
import ConfirmPayment from '../components/payment/ConfirmPayment.vue'
import VoidPayment from '../components/payment/VoidPayment.vue'
import VoidPaidPayment from '../components/payment/VoidPaidPayment.vue'
import CancelPayment from '../components/payment/CancelPayment.vue'
import CancelPaidPayment from '../components/payment/CancelPaidPayment.vue'
import UpdatePaymentRequest from '../components/units/UpdatePaymentRequest.vue'
import CollectUtilities from '../components/units/CollectUtilities.vue'
import Invoice from '../components/Invoice.vue'
import CombinedPayments from '../components/payment/CombinedPayments.vue'
import SecurityDepositTable from '../components/tables/SecurityDepositTable.vue'
import { nanoid } from 'nanoid/non-secure'
import PaymentRequestTable from '../components/tables/PaymentRequestTable.vue'
import PaidPaymentTable from '../components/tables/PaidPaymentTable.vue'

export default {
  components: {
    ConfirmPayment,
    VoidPayment,
    VoidPaidPayment,
    CancelPayment,
    CancelPaidPayment,
    UpdatePaymentRequest,
    CollectUtilities,
    Invoice,
    CombinedPayments,
    SecurityDepositTable,
    PaymentRequestTable,
    PaidPaymentTable
  },
  props: ['username'],
  data() {
    return {
      publicPath: process.env.BASE_URL,
      cropperLabels: {
        submit: 'Submit',
        cancel: 'Cancel'
      },
      user: null,
      violationOptions: {},
      violationsLimit: 0,
      violationsTotal: 0,
      paymentOptions: {},
      paymentsLimit: 0,
      paymentsTotal: 0,
      total: 0,
      loading: true,
      snackbar: false,
      snackMessage: '',
      timeout: 5000,
      balance: 0,
      expandedPayments: [],
      allExpanded: false,
      pendingBalance: 0,
      showAmountDue: false,
      transactionItems: [
        {
          icon: 'mdi-cash-plus',
          title: 'Create Payment Request',
          action: this.createPaymentRequest
        },
        {
          icon: 'mdi-file-document',
          title: 'Generate Statement of Account',
          action: this.generateSOA
        },
        {
          icon: 'mdi-calculator',
          title: 'Create Combined Payment',
          action: this.openCombinedPayments
        }
      ],
      securityDepositSearchTrigger: false,
      paymentsSearchTrigger: false
    }
  },
  computed: {
    violationHeaders() {
      return this.$store.getters['violations/getUserViolationHeaders']
    },
    violations() {
      return this.$store.getters['violations/getViolations']
    },
    paymentHeaders() {
      return this.$store.getters['payments/getUserPaymentHeaders']
    },
    tenantPayments() {
      return this.$store.getters['payments/getTenantPayments']
    },
    paymentAccess() {
      return this.$root.$roleAccess('payment').access
    },
    paymentrequestAccess() {
      return this.$root.$roleAccess('paymentrequest').access
    },
    paidpaymentAccess() {
      return this.$root.$roleAccess('paidpayment').access
    },
    getTenantNameWithUsername() {
      const alias = this.user.alias
      let name = ''
      if (alias) {
        return alias
      }
      if (this.user.firstName) {
        name += this.user.firstName + ' '
      }
      if (this.user.lastName) {
        name += this.user.lastName + ' '
      }
      if (this.user.username) {
        name += `(${this.user.username})`
      }
      return name || 'N/A'
    },
    violationsAccess() {
      return this.$root.$roleAccess('violations').access
    },
    securityDepositAccess() {
      return this.$root.$roleAccess('security_deposit').access
    },
    amountDue() {
      return this.pendingBalance - this.balance
    }
  },
  watch: {
    violationOptions: {
      async handler() {
        this.$store
          .dispatch('violations/fetchSelectedUserViolationsAction', {
            page: this.violationOptions.page,
            limit: this.violationOptions.itemsPerPage,
            sortBy: this.violationOptions.sortBy,
            sortDesc: this.violationOptions.sortDesc,
            tenantId: this.user.userId
          })
          .then((data) => {
            this.loading = false
            this.violationsLimit = data.limit
            this.violationsTotal = data.count
          })
      }
    },
    paymentOptions: {
      async handler() {
        this.reloadPayments()
      }
    }
  },
  async mounted() {
    await this.reload()
  },
  methods: {
    getName() {
      if (this.user.alias) return this.user.alias
      let firstName = this.user.firstName ? this.user.firstName : ''
      let lastName = this.user.lastName ? this.user.lastName : ''

      return `${firstName} ${lastName}`
    },
    getSubscriptionExpireText() {
      if (this.user.subscriptionEnd) {
        return `Subscription Expires on: ${dayjs(
          this.user.subscriptionEnd
        ).format('MMMM D, YYYY')}`
      }
    },
    profilePictureUploadHandler(cropper) {
      cropper.getCroppedCanvas().toBlob(async (blob) => {
        try {
          await this.$store.dispatch('users/updateUserProfilePictureAction', {
            profilePicture: blob
          })

          // this.$router.go()
          window.location.reload()
        } catch (err) {
          this.loading = false
          this.snackMessage = err
          this.snackbar = true
        }
      })
    },
    downloadAttachments(attachments) {
      for (let i = 0; i < attachments.length; i++) {
        window.open(attachments[i].url)
      }
    },
    async setPaid(item) {
      this.$refs.confirmPayment.open(item).then(() => {
        this.reload()
      })
    },
    async updatePayment(item) {
      this.$refs.updatePaymentRequest.open(item).then(async () => {
        this.reload()
      })
    },
    voidPayment(item) {
      this.$refs.voidPayment.open(item).then(() => {
        this.reload()
      })
    },
    cancelPayment(item) {
      this.$refs.cancelPayment.open(item).then(() => {
        this.reload()
      })
    },
    getParsedDate(date) {
      if (date) {
        return dayjs(date).format('MMMM DD, YYYY')
      }
      return ''
    },
    async resendActivation() {
      this.loading = true
      await this.$store.dispatch('users/resendActivationAction', {
        userId: this.user.userId
      })

      this.$root.$confirm({
        title: 'Resend Activation',
        message: `The activation email has been resent.`,
        reconfirmOnly: true,
        confirmText: 'OK'
      })
      this.loading = false
    },
    formatCurrency(value) {
      if (value == null || isNaN(value)) return ''
      return parseFloat(value / 100).toLocaleString('en-PH', {
        style: 'currency',
        currency: 'PHP'
      })
    },
    statusClass(status) {
      if (status === 'Pending') {
        return 'red--text caption'
      } else {
        return 'caption'
      }
    },
    async generateSOA() {
      const tenantId = this.user.userId
      this.$root.$setLoading(true)
      const asyncJob = await this.$store.dispatch(
        'asyncJobs/createSOAAsyncJob',
        { tenantId }
      )
      this.$root.$setLoading(false)
      if (!asyncJob || !asyncJob.jobId) {
        this.$root.$confirm({
          title: 'Error',
          message: 'Failed to generate Statement of Account. Please try again.',
          options: {
            color: 'error'
          }
        })
        return
      }
      this.$router.push(`/asyncJobs/${asyncJob.jobId}`)
    },
    createPaymentRequest() {
      this.$refs.collectUtilities
        .open({
          tenantId: this.user.userId,
          tenant_details: this.user,
          bedName: ''
        })
        .then(() => {
          this.reload()
        })
    },
    showPayment: function (item) {
      this.$refs.invoice.open({ item: item })
    },
    paidItemRow(isExpanded) {
      if (isExpanded) return 'row-item-expanded'
      else return
    },
    expandedItemRow(index, items) {
      // if(index === 0) return 'expand-item'
      if (index >= items.length - 1) return 'expand-item bottom-border'
      return 'expand-item'
    },
    cancelPaidPayment(item) {
      this.$refs.cancelPaidPayment.open(item).then(() => {
        this.reload()
      })
    },
    voidPaidPayment(item) {
      this.$refs.voidPaidPayment.open(item).then(() => {
        this.reload()
      })
    },
    paymentDetailItemAmountPaid(payment, paidPaymentId) {
      const items = payment.paidItems || []
      return (
        items
          .filter((item) => item.paidPaymentId === paidPaymentId)
          .reduce((a, b) => a + b.amount, 0) ||
        payment.amountPaid ||
        0
      )
    },
    expandAll() {
      this.expandedPayments = this.tenantPayments
      this.allExpanded = true
    },
    collapseAll() {
      this.expandedPayments = []
      this.allExpanded = false
    },
    generateId() {
      return nanoid(5)
    },
    hasPartialPayments(item) {
      return item.payment_details.some((p) => p.status === 'Partial Payment')
    },
    showBalance(value) {
      this.showAmountDue = value
    },
    openCombinedPayments() {
      this.$refs.combinedPayments
        .open({
          bedDetails: {
            balance: this.balance,
            tenantId: this.user.userId
          }
        })
        .then(async (success) => {
          if (success) await this.reload()
        })
    },
    async reload() {
      this.loading = true
      try {
        await this.$store.dispatch('users/fetchSelectedUserAction', {
          username: this.username
        })
        this.user = this.$store.getters['users/getSelectedUser']

        this.balance = await this.$store.dispatch(
          'units/fetchTenantBalanceAction',
          {
            tenantId: this.user.userId
          }
        )

        this.$store
          .dispatch('violations/fetchSelectedUserViolationsAction', {
            page: 1,
            limit: this.violationOptions.itemsPerPage,
            tenantId: this.user.userId
          })
          .then((data) => {
            this.loading = false
            this.violationsLimit = data.limit
            this.violationsTotal = data.count
          })
        this.pendingBalance = await this.$store.dispatch(
          'payments/fetchBalanceAction',
          { userId: this.user.userId }
        )
        this.reloadPayments()
        this.reloadSecurityDeposit()
      } catch (err) {
        this.snackMessage = err.data || err
        this.snackbar = true
      }
    },
    reloadSecurityDeposit() {
      this.securityDepositSearchTrigger = !this.securityDepositSearchTrigger
    },
    reloadPayments() {
      this.paymentsSearchTrigger = !this.paymentsSearchTrigger
    }
  }
}
</script>

<style scoped>
.v-card.card-content {
  border-radius: 12px !important;
}
.v-menu__content {
  box-shadow: 0px 5px 5px -3px rgba(0, 0, 0, 0.05),
    0px 8px 10px 1px rgba(0, 0, 0, 0.05), 0px 3px 14px 2px rgba(0, 0, 0, 0.1) !important;
}
.v-input {
  border-radius: 10px;
}
table tbody tr td {
  /*border: none !important;
    border-bottom: none !important;*/
  border-color: #eeeeee !important;
}
p {
  margin-bottom: 0;
}
.row-item-expanded td p {
  font-weight: bold;
}
.row-item-expanded td {
  border: none !important;
}
.expand-item td p {
  font-weight: 300;
  font-size: 11px !important;
}
.expand-item td {
  border: none !important;
}
tr.top-border td {
  border-top: thin solid #ccc !important;
}

tr.bottom-border td {
  /*border-bottom: thin solid #CCC !important;*/
  border-bottom: thin solid rgba(0, 0, 0, 0.12) !important;
}

tr.x-border td:first-child {
  border-left: thin solid #ccc !important;
}

tr.x-border td:last-child {
  border-right: thin solid #ccc !important;
}
.table-footer-prepend {
  margin-top: -54px;
  height: 48px;
}
</style>
